import React from "react";
import {
    Alert,
    AlertTitle,
    alpha,
    Chip,
    FormControlLabel,
    Checkbox,
    FormGroup,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { FORM3_MIN_SUBJECTS } from "../../utils/form3SubjectSelection";
import { dedupeSubjectsById } from "../../utils/dedupeSubjects";

/**
 * Pick at least 8 subjects at enrollment (Form 3 or Form 4).
 */
export default function Form3SubjectPicker({
    subjects = [],
    selectedIds = [],
    onToggle,
    loading = false,
    compact = false,
    formLevel = "Form 3",
}) {
    const catalog = dedupeSubjectsById(subjects);
    const selectedSet = new Set(selectedIds);
    const selectedCount = selectedSet.size;
    const canPick = catalog.length >= FORM3_MIN_SUBJECTS;
    const formLabel = formLevel === "Form 4" ? "Form 4" : "Form 3";

    if (!canPick && !loading) {
        return (
            <Alert severity="warning" sx={{ borderRadius: 2, mb: compact ? 0 : 2 }}>
                <AlertTitle>{formLabel} subjects not ready</AlertTitle>
                Add at least {FORM3_MIN_SUBJECTS} subjects to this {formLabel} class before enrolling pupils.
            </Alert>
        );
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                mb: compact ? 0 : 2,
                borderRadius: 2,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <MenuBookOutlinedIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight={700}>
                    {formLabel} subject line
                </Typography>
                <Chip
                    size="small"
                    label={`${selectedCount} / ${FORM3_MIN_SUBJECTS} minimum`}
                    color={selectedCount >= FORM3_MIN_SUBJECTS ? "success" : "warning"}
                    variant="outlined"
                    sx={{ fontWeight: 700 }}
                />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Each new {formLabel} pupil must choose at least {FORM3_MIN_SUBJECTS} subjects when enrolling.
            </Typography>
            {loading ? (
                <Typography variant="body2" color="text.secondary">
                    Loading subjects…
                </Typography>
            ) : (
                <FormGroup>
                    {catalog.map((sub) => {
                        const sid = sub._id || sub.id;
                        return (
                            <FormControlLabel
                                key={sid}
                                control={
                                    <Checkbox
                                        checked={selectedSet.has(sid)}
                                        onChange={() => onToggle?.(sid)}
                                    />
                                }
                                label={
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Typography fontWeight={600}>{sub.subName}</Typography>
                                        {sub.isElective ? (
                                            <Chip size="small" label="Elective" variant="outlined" />
                                        ) : null}
                                    </Stack>
                                }
                                sx={{ alignItems: "flex-start", ml: 0 }}
                            />
                        );
                    })}
                </FormGroup>
            )}
        </Paper>
    );
}
